import React, { useState, useMemo } from 'react';
import { useAuth } from '../../../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { saveEstimate } from '../../../../services/estimateService';
import { useAnalyticsEvents } from '../../../../hooks/useAnalytics';
import { Calculator } from 'lucide-react';
import { calculateQuickEstimate } from '../../../../utils/quickEstimateCalculator';

interface ResultStepProps {
  value: any;
  onReset: () => void;
  onBack: () => void;
}

export const ResultStep: React.FC<ResultStepProps> = ({ value, onReset, onBack }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { trackEstimationEvent, trackButtonClick } = useAnalyticsEvents();
  const [error, setError] = useState<string | null>(null);

  const estimate = useMemo(() => {
    try {
      const result = calculateQuickEstimate(value);
      trackEstimationEvent('result_calculated', 'quick');
      return result;
    } catch (err) {
      console.error('Erreur de calcul:', err);
      setError('Une erreur est survenue lors du calcul de l\'estimation');
      return null;
    }
  }, [value]);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0
    }).format(price);
  };

  const handleSaveEstimate = async () => {
    if (!user) {
      navigate('/register');
      return;
    }

    try {
      const estimateData = {
        type: 'quick' as const,
        propertyType: value.propertyType,
        area: value.area,
        location: {
          address: value.location?.address || '',
          postcode: value.location?.postcode || '',
          city: value.location?.city || '',
          department: value.location?.postcode?.substring(0, 2) || ''
        },
        renovationLevel: value.renovationLevel,
        keyElements: value.keyElements || {
          windows: false,
          kitchen: false,
          bathroom: false
        },
        networks: value.networks,
        finishingLevel: value.finishingLevel,
        accessibility: value.accessibility,
        constraints: value.constraints,
        buildingDetails: value.buildingDetails,
        priceRange: estimate,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      console.log('💾 Saving quick estimate with structured data:', estimateData);

      await saveEstimate(user.uid, estimateData);
      trackEstimationEvent('estimate_saved', 'quick');
      navigate('/dashboard');
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      setError('Erreur lors de la sauvegarde de l\'estimation');
    }
  };

  if (!estimate) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">
          {error || 'Impossible de calculer l\'estimation avec les données fournies'}
        </p>
        <button onClick={onBack} className="mt-4 btn btn-secondary">
          Retour
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="text-base font-semibold text-primary">Estimation des coûts</h2>
      
      <div className="card bg-gradient-to-br from-primary/5 to-primary/10">
        <p className="text-sm mb-2">Le coût estimé des travaux se situe entre :</p>
        <p className="text-2xl font-bold text-primary mb-4">
          {formatPrice(estimate.min)} - {formatPrice(estimate.max)}
        </p>
        <p className="text-sm text-neutral-600">
          Cette estimation est donnée à titre indicatif et peut varier selon les spécificités de votre projet.
        </p>
        <div className="mt-4 pt-4 border-t border-primary/20">
          <p className="text-sm text-neutral-700 leading-relaxed">
            Les prix dans SimulReno sont basés sur le marché à début 2026. Ils sont volontairement surévalués d'environ 10%. Il est toujours préférable de provisionner une marge pour les aléas lorsque l'on se lance dans une rénovation.
          </p>
        </div>
      </div>

      <div className="bg-orange-500 text-white rounded-lg p-6 shadow-lg">
        <h3 className="text-base font-semibold mb-2">Besoin d'une estimation plus précise ?</h3>
        <p className="text-sm mb-4">
          Obtenez une estimation détaillée prenant en compte tous les aspects de votre projet de rénovation
        </p>
        <button
          onClick={() => navigate('/estimation/nouvelle')}
          className="w-full bg-white text-orange-500 py-3 px-6 rounded-lg font-medium hover:bg-orange-50 transition-colors flex items-center justify-center"
        >
          <Calculator className="h-5 w-5 mr-2" />
          Obtenir une estimation détaillée
        </button>
      </div>

      <div className="flex flex-col space-y-4">
        <button
          onClick={handleSaveEstimate}
          className="btn bg-primary text-white hover:bg-primary-dark"
        >
          {user ? 'Enregistrer cette estimation' : 'Créer un compte pour sauvegarder'}
        </button>

        <div className="flex justify-between">
          <button onClick={onBack} className="btn btn-secondary">
            Précédent
          </button>
          <button onClick={onReset} className="btn btn-primary">
            Nouvelle estimation
          </button>
        </div>
      </div>

      {error && (
        <div className="text-red-600 text-center p-4 bg-red-50 rounded-lg">
          {error}
        </div>
      )}
    </div>
  );
};

export default ResultStep;